import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { formatDate } from '../../lib/dates';
import type { NewsFeedItem } from '../../types';
import { categoryInfo, fileKindLabel, formatBytes } from './categories';
import { FileKindIcon } from './FileKindIcon';

interface NewsItemCardProps {
    item: NewsFeedItem;
    /** Compact variant for the dashboard. */
    compact?: boolean;
}

/** One row in the news feed: an article (links to /news/:id) or a document (opens the file). */
export function NewsItemCard({ item, compact = false }: NewsItemCardProps) {
    const dateStr = formatDate(item.published_at);
    const cat = categoryInfo(item.category);

    if (item.kind === 'article') {
        return (
            <Link to={`/news/${item.id}`} className={`news-card card ${compact ? 'news-card-compact' : ''}`}>
                <div className="news-card-content">
                    {item.thumb_url ? (
                        <img src={item.thumb_url} alt="" className="news-thumb" loading="lazy" />
                    ) : (
                        <div className={`news-icon ${cat.tone}`}>{cat.icon}</div>
                    )}
                    <div className="news-info">
                        <h4 className="news-title">{item.title}</h4>
                        <div className="news-meta">
                            <span className={`news-badge ${cat.tone}`}>{cat.short}</span>
                            <span className="news-date">{dateStr}</span>
                        </div>
                    </div>
                    <ChevronRight className="news-arrow" size={20} aria-hidden="true" />
                </div>
            </Link>
        );
    }

    // A document is served by row id, so a plain link is enough: the Worker
    // re-checks the viewer on every fetch and the object key never leaves it.
    const fileName = item.file_name ?? item.title;
    const size = formatBytes(item.file_size);
    return (
        <a
            href={`/api/documents/${item.id}/file`}
            target="_blank"
            rel="noreferrer"
            className={`news-card card news-card-doc ${compact ? 'news-card-compact' : ''}`}
            aria-label={`${item.title} (${fileKindLabel(fileName)}${size ? `, ${size}` : ''}) — opens in a new tab`}
        >
            <div className="news-card-content">
                <div className={`news-icon file-${cat.tone}`}><FileKindIcon fileName={fileName} /></div>
                <div className="news-info">
                    <h4 className="news-title">{item.title}</h4>
                    <div className="news-meta">
                        <span className={`news-badge ${cat.tone}`}>{cat.short}</span>
                        <span className="news-date">{dateStr}</span>
                        <span className="news-file-meta">{fileKindLabel(fileName)}{size ? ` · ${size}` : ''}</span>
                    </div>
                </div>
                <ExternalLink className="news-arrow" size={18} aria-hidden="true" />
            </div>
        </a>
    );
}
